import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Mail, Clock, Copy, XCircle, ExternalLink, Trash2, Plus } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { EmptyState } from '@/components/ui/empty-state';
import { LoadingPage } from '@/components/ui/loading';
import { formatDate, formatTimeUntil } from '@/lib/utils';
import type { Organization, Invite } from '@/types/api';
import { UserRole } from '@/types/api';
import { addOrgMember, getOrganizationById, getOrgInvites, revokeInvite, removeOrgMember, deleteOrganization } from '../api/organizations-api';
import { roleLabel } from '@/lib/display-labels';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { getUsers } from '@/features/users/api/users-api';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { useOrg } from '@/store/org-context';
import { useActiveRole } from '@/features/auth/hooks/use-active-role';
import { useToast } from '@/components/ui/use-toast';
import type { User } from '@/types/api';
import { AddUserModal } from './add-user-modal';

const roleVariants: Record<UserRole, 'default' | 'success' | 'warning' | 'error' | 'info' | 'secondary'> = {
  [UserRole.Owner]: 'secondary',
  [UserRole.Admin]: 'info',
  [UserRole.Manager]: 'warning',
  [UserRole.Viewer]: 'default',
};

// Backend returns dependent counts under camelCase keys like
// { properties: 3, devices: 12, gateways: 1, reports: 0 }. Map each to a
// human-readable noun the toast can splice into a sentence.
const DEPENDENT_LABELS: Record<string, { singular: string; plural: string }> = {
  properties: { singular: 'property', plural: 'properties' },
  devices: { singular: 'device', plural: 'devices' },
  gateways: { singular: 'gateway', plural: 'gateways' },
  reports: { singular: 'report', plural: 'reports' },
  users: { singular: 'user', plural: 'users' },
  auditLogs: { singular: 'audit log entry', plural: 'audit log entries' },
};

function pluralizeDependent(key: string, count: number): string {
  const label = DEPENDENT_LABELS[key];
  if (!label) return key;
  return count === 1 ? label.singular : label.plural;
}

function joinList(parts: string[]): string {
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
}

export function OrganizationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { setActiveOrg } = useOrg();
  const activeRole = useActiveRole();
  const { showToast } = useToast();

  // Gate the invite affordance to roles that can manage org membership.
  // Viewer is read-only; Manager/Admin/Owner all map to backend `[ActiveRole("Owner,Admin,Manager")]`
  // on the invite endpoint.
  const canInvite =
    activeRole === UserRole.Owner ||
    activeRole === UserRole.Admin ||
    activeRole === UserRole.Manager;

  const [org, setOrg] = useState<Organization | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);
  const [revokingInvite, setRevokingInvite] = useState<Invite | null>(null);
  const [removingUser, setRemovingUser] = useState<User | null>(null);
  const [confirmDeleteOrg, setConfirmDeleteOrg] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);

  useEffect(() => {
    if (!id) return;
    loadAll(id);
  }, [id]);

  async function loadAll(orgId: string) {
    setLoading(true);
    try {
      const [orgData, usersData, invitesData] = await Promise.all([
        getOrganizationById(orgId),
        getUsers({ perPage: 100, orgId }),
        getOrgInvites(orgId),
      ]);
      setOrg(orgData);
      setUsers(usersData.items);
      setInvites(invitesData);
    } catch (error) {
      console.error('Failed to load organization:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleRevokeInviteConfirm() {
    if (!revokingInvite) return;
    const invite = revokingInvite;
    setRevokingInvite(null);
    await revokeInvite(invite.id);
    if (id) loadAll(id);
  }

  async function handleRemoveMemberConfirm() {
    if (!id || !removingUser) return;
    const user = removingUser;
    setRemovingUser(null);
    await removeOrgMember(id, user.id);
    loadAll(id);
  }

  async function handleDeleteOrgConfirm() {
    if (!id) return;
    setConfirmDeleteOrg(false);
    try {
      await deleteOrganization(id);
      navigate('/organizations');
    } catch (error) {
      console.error('Failed to delete organization:', error);
      // 409: org has dependents → list which resource types block the delete.
      // 5xx: any unexpected server error → generic recovery message.
      // Anything else (network, 4xx other than 409) falls through to a generic toast too.
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        const data = error.response.data as { dependents?: Record<string, number> } | undefined;
        const dependents = data?.dependents ?? {};
        const parts = Object.entries(dependents)
          .filter(([, count]) => typeof count === 'number' && count > 0)
          .map(([key, count]) => `${count} ${pluralizeDependent(key, count)}`);
        showToast({
          variant: 'error',
          title: "Can't delete organization",
          description: parts.length > 0
            ? `This organization still has ${joinList(parts)}. Remove these first.`
            : 'This organization has active resources that must be removed first.',
          durationMs: 9000,
        });
      } else {
        showToast({
          variant: 'error',
          title: 'Delete failed',
          description: 'Something went wrong deleting this organization. Please try again or contact support.',
        });
      }
    }
  }

  async function handleAddUserSubmit(email: string, role: UserRole) {
    if (!id) return;
    await addOrgMember(id, { email, role });
    setShowAddUser(false);
    // Refresh members + pending invites so the new invite shows up immediately.
    loadAll(id);
  }

  if (loading || !org) return <LoadingPage />;

  const pendingInvites = invites.filter(i => !i.isAccepted && new Date(i.expiresAt) > new Date());
  const isEmpty = users.length === 0 && pendingInvites.length === 0;

  return (
    <>
      <div className="p-6 lg:p-8">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Back + Header */}
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/organizations')} className="mt-0.5">
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{org.name}</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 font-mono mt-0.5">{org.slug}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setActiveOrg(org.id, org.name);
                  navigate('/dashboard');
                }}
              >
                <ExternalLink className="w-4 h-4" />
                View Organization
              </Button>
              <Button
                variant="outline"
                onClick={() => setConfirmDeleteOrg(true)}
                className="text-error-600 hover:text-error-700 border-error-200 hover:border-error-300"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: 'Users', value: org.userCount },
              { label: 'Properties', value: org.propertyCount },
              { label: 'Devices', value: org.deviceCount },
            ].map(stat => (
              <Card key={stat.label} className="p-4 text-center">
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stat.value}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</div>
              </Card>
            ))}
          </div>

          {/* Users + Invites Table */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                Members
                {pendingInvites.length > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-xs bg-amber-100 text-amber-700 rounded-full">
                    {pendingInvites.length}
                  </span>
                )}
              </h2>
              {canInvite && (
                <Button
                  size="sm"
                  onClick={() => setShowAddUser(true)}
                  data-testid="add-user-button"
                >
                  <Plus className="w-4 h-4" />
                  Add User
                </Button>
              )}
            </div>
            <Card>
              {isEmpty ? (
                <EmptyState icon={<Users className="w-8 h-8" />} title="No members" description="Invite users to this organization." />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map(u => (
                      <TableRow key={u.id}>
                        <TableCell className="font-medium">{u.firstName} {u.lastName}</TableCell>
                        <TableCell className="text-gray-500 dark:text-gray-400">{u.email}</TableCell>
                        <TableCell>
                          <Badge variant={roleVariants[u.role]}>{roleLabel[u.role]}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={u.isActive ? 'success' : 'error'}>
                            {u.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-gray-500 dark:text-gray-400">{formatDate(u.createdAt)}</TableCell>
                        <TableCell>
                          {u.id !== currentUser?.id && (
                            <div className="flex items-center justify-end">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setRemovingUser(u)}
                                className="text-error-600 hover:text-error-700"
                                title="Remove from organization"
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {pendingInvites.map(invite => (
                      <TableRow key={invite.id} className="opacity-75">
                        <TableCell className="text-gray-400 dark:text-gray-500 italic">—</TableCell>
                        <TableCell className="text-gray-500 dark:text-gray-400">
                          <span className="flex items-center gap-1.5">
                            <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                            {invite.email}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge variant={roleVariants[invite.role]}>{roleLabel[invite.role]}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="warning">
                            <Clock className="w-3 h-3 mr-1" />
                            Invited · expires in {formatTimeUntil(invite.expiresAt)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-gray-500 dark:text-gray-400">{formatDate(invite.createdAt)}</TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              title="Copy invite link"
                              onClick={() => navigator.clipboard.writeText(`${window.location.origin}/accept-invite?token=${invite.token}`)}
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setRevokingInvite(invite)}
                              className="text-error-600 hover:text-error-700"
                              title="Cancel invite"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>
          </div>
        </div>
      </div>


      <ConfirmDialog
        isOpen={!!revokingInvite}
        title="Cancel Invite"
        description={`Cancel the invite sent to ${revokingInvite?.email}? They won't be able to use the link.`}
        confirmLabel="Cancel Invite"
        onConfirm={handleRevokeInviteConfirm}
        onCancel={() => setRevokingInvite(null)}
      />

      <ConfirmDialog
        isOpen={!!removingUser}
        title="Remove Member"
        description={`Remove ${removingUser?.firstName} ${removingUser?.lastName} from this organization? They will lose access immediately.`}
        confirmLabel="Remove"
        onConfirm={handleRemoveMemberConfirm}
        onCancel={() => setRemovingUser(null)}
      />

      <ConfirmDialog
        isOpen={confirmDeleteOrg}
        title="Delete Organization"
        description={`Permanently delete "${org.name}" and all its properties, devices, and data? This cannot be undone.`}
        confirmLabel="Delete Organization"
        onConfirm={handleDeleteOrgConfirm}
        onCancel={() => setConfirmDeleteOrg(false)}
      />

      <AddUserModal
        isOpen={showAddUser}
        onClose={() => setShowAddUser(false)}
        onSubmit={handleAddUserSubmit}
        callerRole={activeRole}
      />

    </>
  );
}
